import { useMutation } from '@apollo/client/react';
import Compressor from 'compressorjs';
import { useCallback } from 'react';
import uploadGCSSignedUrl from '../uploadGCSSignedUrl';
import UPLOAD_PAGE_ATTACHMENT from './mutations/UploadFileAttachment';

interface UseFileAttachmentSubmitOpts {
  partnerId: string;
  onProgress?: () => void;
  onUpload?: (
    readUrl: string,
    filename: string,
    mimetype: string,
  ) => void | Promise<void>;
  onError?: (message: string) => void;
  onFinish?: () => void;
  allowedTypes?: RegExp[];
}

interface AttachmentInputEvent {
  target: {
    files: Array<File> | FileList | null;
  };
}

const useFileAttachmentSubmit = function ({
  partnerId,
  onProgress = () => {},
  onUpload = () => {},
  onError = () => {},
  onFinish = () => {},
  allowedTypes = [
    /application\/*/,
    /image\/*/,
    /text\/*/,
    /video\/mp4/,
    /audio\/mpeg/,
  ],
}: UseFileAttachmentSubmitOpts) {
  const [uploadFileAttachment] = useMutation(UPLOAD_PAGE_ATTACHMENT);
  return useCallback(
    async ({ target: { files } }: AttachmentInputEvent) => {
      const file = files?.[0];
      if (!file) return null;

      let blob: File | Blob = file;
      if (!allowedTypes.some((type) => type.test(file.type))) {
        onError('Oops, you can only upload images, videos and documents.');
        return null;
      }

      onProgress();

      try {
        if (/image\/*/.test(file.type)) {
          blob = await new Promise<File | Blob>((resolve, reject) => {
            Compressor.create(file, {
              quality: 0.8,
              mimeType: file.type,
              maxWidth: 2000,
              maxHeight: 1725,
              success: resolve,
              error: reject,
            });
          });
        }

        const filename = file.name;
        const mimetype = file.type;

        const { data } = await uploadFileAttachment({
          variables: {
            id: partnerId,
            input: {
              filename,
              mimetype,
            },
          },
        });

        if (!data?.uploadFileAttachment) {
          onError('Upload failed');
          return null;
        }
        const { writeUrl, readUrl, maxBytes, acl } = data.uploadFileAttachment;

        if (blob.size > maxBytes) {
          const maxMegaBytes = maxBytes / 1024 / 1024;
          throw new Error(
            `The file is too big, maximum size is ${maxMegaBytes}`,
          );
        }

        await uploadGCSSignedUrl({
          contentType: mimetype,
          writeUrl,
          blob,
          maxBytes,
          acl,
        });

        await onUpload(readUrl, filename, mimetype);
        onFinish();
        return readUrl;
      } catch (err) {
        onError((err as Error).message);
        return null;
      }
    },
    [
      partnerId,
      uploadFileAttachment,
      allowedTypes,
      onError,
      onUpload,
      onFinish,
      onProgress,
    ],
  );
};

export default useFileAttachmentSubmit;
